import { useBookingModuleContext } from "PFApp/booking/parts/providers/booking_module_context_provider";
import { usePastDaysLimit } from "PFApp/hooks";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import { Typography } from "PFComponents/typography";
import { useBookingsBulkMove } from "PFCore/hooks/queries/bookings/bulk_operation/use_bookings_bulk_move";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { BookingsBulkMovePayload } from "PFCore/services/bookings_bulk_move";
import { ONE_DAY_MINUTES } from "PFCore/utilities/time_consts";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { UNDETERMINED_FUTURE } from "../../../constants/dates";
import { useGrowl } from "../../../use_growl";
import { useBookingRowsContext } from "../../parts/overview/context/booking_rows_context";
import { EditMode } from "../../types/edit_mode";
import { BulkMoveAction } from "./actions/bulk_move_action";
import { useBulkUpdateContext } from "./bulk_update_context_provider";
import css from "./bulk_update_modal.module.scss";
import { useInvalidateSelectedActivitiesWarnings } from "./use_invalidate_selected_activities_warnings";

export const BulkUpdateModal = () => {
  const { t } = useTranslation(["bookingModule", "translation"]);
  const pastDaysLimit = usePastDaysLimit();
  const {
    modal: { close }
  } = useBulkUpdateContext();

  const {
    setEditMode,
    selectEditMode: { selection, bookingsCount, bookingsRangeMinDate, bookingsRangeMaxDate }
  } = useBookingRowsContext();

  const { setHeaderDisabled } = useBookingModuleContext();

  const { invalidateSelectedActivitiesWarnings } = useInvalidateSelectedActivitiesWarnings();

  const { utc, formatISODate } = useDateFormatter();
  const [date, setDate] = useState(formatISODate(utc(bookingsRangeMinDate)));
  const growl = useGrowl();
  const { mutate: bulkUpdate } = useBookingsBulkMove({
    onSuccess: () => {
      invalidateSelectedActivitiesWarnings();
      setEditMode(EditMode.Disabled);
      setHeaderDisabled(false);
      close();
      growl({
        message: t("bookingModule:selectMode.bulkOperationModal.successMessage"),
        kind: "success"
      });
    }
  });

  if (!selection) {
    return null;
  }

  const offsetInMinutes = utc(date).diff(utc(bookingsRangeMinDate), "days") * ONE_DAY_MINUTES;

  const handleOk = () => {
    const payload: BookingsBulkMovePayload = {
      filters: selection.filters,
      exclude: selection.exclude,
      updates: {
        offsetInMinutes
      },
      dateRange: {
        min: formatISODate(utc().subtract(pastDaysLimit, "days")),
        max: formatISODate(UNDETERMINED_FUTURE)
      }
    };

    bulkUpdate(payload);
  };

  return (
    <ConfirmModal
      title={t("bookingModule:selectMode.bulkOperationModal.title")}
      labelOK={t("bookingModule:selectMode.bulkOperationModal.confirm")}
      labelCancel={t("translation:cancel")}
      handleOK={handleOk}
      handleClose={close}
      style={{ maxWidth: 800 }}
      footerLabel={t("bookingModule:selectMode.bulkOperationModal.footerLabel")}
    >
      <div className={css.root}>
        <Typography variant="bodyRegular" tag="span">
          {t("bookingModule:selectMode.bulkOperationModal.selectedBookingsMessage", {
            count: bookingsCount ?? 0,
            minDate: formatISODate(utc(bookingsRangeMinDate)),
            maxDate: formatISODate(utc(bookingsRangeMaxDate))
          })}
        </Typography>
        <BulkMoveAction date={date} setDate={setDate} />
      </div>
    </ConfirmModal>
  );
};
